<template>
  <section class="container-fluid py-3">
    <form class="row g-2 align-items-end mb-3" @submit.prevent="applyFilters">
      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Lifecycle</label>
        <select v-model="lifecycle" class="form-select" @change="onLifecycleChanged">
          <option v-for="value in lifecycleOptions" :key="value" :value="value">{{ value }}</option>
        </select>
      </div>

      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Ownership</label>
        <select v-model="ownership" class="form-select">
          <option value="">All</option>
          <option v-for="value in ownershipOptions" :key="value" :value="value">{{ value }}</option>
        </select>
      </div>

      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Eligibility</label>
        <select v-model="eligibilityStatus" class="form-select">
          <option value="">All</option>
          <option v-for="value in eligibilityOptions" :key="value" :value="value">{{ value }}</option>
        </select>
      </div>

      <div class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Project Type</label>
        <select v-model="projectType" class="form-select">
          <option value="">All</option>
          <option v-for="value in projectTypeOptions" :key="value" :value="value">{{ value }}</option>
        </select>
      </div>

      <div v-if="isClosedOnly" class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Standing</label>
        <select v-model="standingStatus" class="form-select">
          <option value="">All</option>
          <option v-for="value in standingOptions" :key="value" :value="value">{{ value }}</option>
        </select>
      </div>

      <div v-if="!isOpenOnly" class="col-12 col-sm-6 col-md-3 col-lg-2">
        <label class="form-label">Since</label>
        <select v-model="sinceYear" class="form-select">
          <option value="">All</option>
          <option v-for="value in sinceYearOptions" :key="value" :value="value">{{ value }}</option>
        </select>
      </div>

      <div class="col-12">
        <div class="row g-2">
          <div class="col-12 col-sm-6 col-md-4 col-lg-2">
            <label class="form-label">Sort By</label>
            <select v-model="sortBy" class="form-select">
              <option value="">Default</option>
              <option v-for="value in activeSortByOptions" :key="value.value" :value="value.value">{{ value.label }}</option>
            </select>
          </div>

          <div class="col-12 col-sm-6 col-md-4 col-lg-2">
            <label class="form-label">Direction</label>
            <select v-model="sortDirection" class="form-select">
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>

          <div class="col-12 col-sm-6 col-md-4 col-lg-2">
            <label class="form-label">Page Size</label>
            <select v-model.number="pageSize" class="form-select" @change="applyFilters">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      <div class="col-auto d-flex gap-2">
        <button type="submit" class="btn btn-primary" :disabled="loading">Apply</button>
        <button type="button" class="btn btn-secondary" :disabled="loading" @click="resetFilters">Reset</button>
      </div>
    </form>

    <div v-if="loading" class="d-flex align-items-center mb-3">
      <div class="spinner-border me-2" role="status"><span class="visually-hidden">Loading...</span></div>
      <span>Loading project standing...</span>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <div v-else>
      <div class="small text-muted mb-2">
        Showing {{ items.length }} of {{ totalCount }} total projects (page {{ page }})
      </div>
      <div v-if="typeCounts.length" class="small text-muted mb-2 d-flex flex-wrap gap-3">
        <span v-for="item in typeCounts" :key="item.ProjectType || 'Unspecified'">
          <strong>{{ item.ProjectType || 'Unspecified' }}</strong>: {{ item.Count }} ({{ formatTypePercent(item.Count) }})
        </span>
      </div>

      <PaginationControl
        v-if="items.length"
        class="mb-2"
        label="Project standing paging top"
        key-prefix="top"
        :page="page"
        :visible-pages="visiblePages"
        :can-prev="canPrev"
        :can-next="canNext"
        @first="goFirst"
        @prev="goPrev"
        @goto="goToPage"
        @next="goNext"
        @last="goLast"
      />

      <div class="project-standing-table-wrap">
        <table v-if="items.length" class="table table-sm table-hover align-middle">
          <thead class="table-light">
            <tr>
              <th scope="col">Project</th>
              <th scope="col">Company</th>
              <th v-if="!isOpenOnly" scope="col" class="text-center">Ownership</th>
              <th v-if="!isOpenOnly" scope="col" class="text-center">Eligibility</th>
              <th v-if="isOpenOnly" scope="col" class="text-center">Projected Utilization</th>
              <th v-if="isOpenOnly" scope="col" class="text-center">Budget Hours</th>
              <th v-if="isOpenOnly" scope="col" class="text-center">Actual Hours</th>
              <th v-if="isOpenOnly" scope="col" class="text-center">Open Ticket Remaining (Actual Hours)</th>
              <th v-if="isOpenOnly" scope="col" class="text-center">Current Utilization</th>
              <th v-if="isClosedOnly" scope="col" class="text-center">Standing</th>
              <th v-if="!isOpenOnly" scope="col" class="text-center">Invoice Balance</th>
              <th v-if="!isOpenOnly" scope="col" class="text-center">Margin %</th>
              <th v-if="!isOpenOnly" scope="col">Date Closed</th>
              <th v-if="canManageProjectStanding" scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in items" :key="row.ProjectId">
              <td>
                <div class="fw-semibold">{{ row.ProjectName }}</div>
                <div class="text-muted small">#{{ row.ProjectId }}<span v-if="row.ProjectType"> • {{ row.ProjectType }}</span></div>
              </td>
              <td>{{ row.CompanyName || '-' }}</td>
              <td v-if="!isOpenOnly" class="text-center">{{ row.Ownership }}</td>
              <td v-if="!isOpenOnly" class="text-center">{{ row.EligibilityStatus }}</td>
              <td v-if="isOpenOnly" class="text-center">
                <span class="badge" :class="projectedUtilizationBadgeClass(row.HoursActual, row.OpenTicketBudgetRemaining, row.BudgetHours)">
                  {{ formatProjectedUtilization(row.HoursActual, row.OpenTicketBudgetRemaining, row.BudgetHours) }}
                </span>
              </td>
              <td v-if="isOpenOnly" class="text-center">{{ formatHours(row.BudgetHours) }}</td>
              <td v-if="isOpenOnly" class="text-center">{{ formatHours(row.HoursActual) }}</td>
              <td v-if="isOpenOnly" class="text-center">{{ formatHours(row.OpenTicketBudgetRemaining) }}</td>
              <td v-if="isOpenOnly" class="text-center">
                <span class="badge" :class="utilizationBadgeClass(row.HoursActual, row.BudgetHours)">
                  {{ formatUtilization(row.HoursActual, row.BudgetHours) }}
                </span>
              </td>
              <td v-if="isClosedOnly" class="text-center">{{ row.StandingStatus }}</td>
              <td v-if="!isOpenOnly" class="text-center">{{ formatCurrency(row.InvoiceBalance) }}</td>
              <td v-if="!isOpenOnly" class="text-center">
                <span class="badge" :class="marginBadgeClass(row.TrueMarginPercent)">
                  {{ formatPercent(row.TrueMarginPercent) }}
                </span>
              </td>
              <td v-if="!isOpenOnly">{{ formatLocalDate(row.CloseDateUtc) }}</td>
              <td v-if="canManageProjectStanding">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="loading || recalculatingProjectIds.has(row.ProjectId)"
                  @click="recalculateProject(row.ProjectId)"
                >
                  <span v-if="recalculatingProjectIds.has(row.ProjectId)">Recalculating...</span>
                  <span v-else>Recalculate</span>
                </button>
                <button
                  type="button"
                  class="btn btn-sm ms-2"
                  :class="row.IsExcluded ? 'btn-outline-success' : 'btn-outline-warning'"
                  :disabled="loading || exclusionUpdatingProjectIds.has(row.ProjectId)"
                  @click="toggleExclusion(row)"
                >
                  <span v-if="exclusionUpdatingProjectIds.has(row.ProjectId)">Saving...</span>
                  <span v-else>{{ row.IsExcluded ? 'Unignore' : 'Ignore' }}</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!items.length" class="alert alert-info" role="alert">
        No projects found for the selected filters.
      </div>

      <PaginationControl
        v-if="items.length"
        label="Project standing paging"
        key-prefix="bottom"
        :page="page"
        :visible-pages="visiblePages"
        :can-prev="canPrev"
        :can-next="canNext"
        @first="goFirst"
        @prev="goPrev"
        @goto="goToPage"
        @next="goNext"
        @last="goLast"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { getCurrentUserInfo, getProjectStandingGroups, getProjectStandingProjectTypes, getProjectStandingTypeCounts, getProjectStandings, recalculateProjectStanding, setProjectStandingExclusion } from '../api'
import PaginationControl from './PaginationControl.vue'
import type {
  ProjectStandingEligibilityStatus,
  ProjectStandingLifecycle,
  ProjectStandingOwnership,
  ProjectStandingRow,
  ProjectStandingStatus,
  ProjectStandingTypeCount,
} from '../types'

const lifecycleOptions: ProjectStandingLifecycle[] = ['ClosedOnly', 'OpenOnly', 'All']
const ownershipOptions: ProjectStandingOwnership[] = ['None', 'Inherited', 'Shared', 'Full']
const eligibilityOptions: ProjectStandingEligibilityStatus[] = ['Eligible', 'NotEligible', 'Excluded']
const standingOptions: ProjectStandingStatus[] = ['WaitingForPayment', 'CreditValidation', 'Complete']
const closedSortByOptions = [
  { value: 'project', label: 'Project' },
  { value: 'company', label: 'Company' },
  { value: 'eligibility', label: 'Eligibility' },
  { value: 'standing', label: 'Standing' },
  { value: 'invoiceBalance', label: 'Invoice Balance' },
  { value: 'margin', label: 'Margin %' },
  { value: 'lastCalculated', label: 'Last Calculated' },
  { value: 'dateClosed', label: 'Date Closed' },
] as const
const openSortByOptions = [
  { value: 'project', label: 'Project' },
  { value: 'company', label: 'Company' },
  { value: 'eligibility', label: 'Eligibility' },
  { value: 'projectedUtilization', label: 'Projected Utilization' },
  { value: 'openTicketBudgetRemaining', label: 'Open Ticket Remaining (Actual Hours)' },
  { value: 'utilization', label: 'Utilization' },
  { value: 'lastCalculated', label: 'Last Calculated' },
  { value: 'dateClosed', label: 'Date Closed' },
] as const
const sinceYearOptions = buildSinceYearOptions()
const projectTypeOptions = ref<string[]>([])

const lifecycle = ref<ProjectStandingLifecycle>('ClosedOnly')
const ownership = ref<ProjectStandingOwnership | ''>('')
const eligibilityStatus = ref<ProjectStandingEligibilityStatus | ''>('')
const standingStatus = ref<ProjectStandingStatus | ''>('')
const sortBy = ref<string>('')
const sortDirection = ref<'asc' | 'desc'>('desc')
const sinceYear = ref<string>('')
const projectType = ref<string>('')

const page = ref(1)
const pageSize = ref(100)
const totalCount = ref(0)
const items = ref<ProjectStandingRow[]>([])
const typeCounts = ref<ProjectStandingTypeCount[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const recalculatingProjectIds = ref<Set<number>>(new Set())
const exclusionUpdatingProjectIds = ref<Set<number>>(new Set())
const canManageProjectStanding = ref(false)

const canPrev = computed(() => page.value > 1)
const canNext = computed(() => page.value * pageSize.value < totalCount.value)
const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / pageSize.value)))
const isOpenOnly = computed(() => lifecycle.value === 'OpenOnly')
const isClosedOnly = computed(() => lifecycle.value === 'ClosedOnly')
const activeSortByOptions = computed(() => (isOpenOnly.value ? openSortByOptions : closedSortByOptions))
const visiblePages = computed(() => {
  const pagesPerWindow = 10
  const start = Math.floor((page.value - 1) / pagesPerWindow) * pagesPerWindow + 1
  const end = Math.min(start + pagesPerWindow - 1, totalPages.value)
  const result: number[] = []
  for (let i = start; i <= end; i += 1) {
    result.push(i)
  }
  return result
})

function formatLocalDate(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value)
}

function formatHours(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value ?? 0)
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(value)
}

function formatUtilization(actualHours: number, budgetHours: number): string {
  if (!budgetHours || budgetHours <= 0) return '-'
  return formatPercent(actualHours / budgetHours)
}

function formatProjectedUtilization(actualHours: number, remainingHours: number, budgetHours: number): string {
  if (!budgetHours || budgetHours <= 0) return '-'
  return formatPercent((actualHours + remainingHours) / budgetHours)
}

function utilizationBadgeClass(actualHours: number, budgetHours: number): string {
  if (!budgetHours || budgetHours <= 0) return 'text-bg-light text-dark'

  const utilization = actualHours / budgetHours
  if (utilization > 1.1) return 'text-bg-danger'
  if (utilization > 1.0) return 'text-bg-warning'
  return 'text-bg-secondary'
}

function projectedUtilizationBadgeClass(actualHours: number, remainingHours: number, budgetHours: number): string {
  if (!budgetHours || budgetHours <= 0) return 'text-bg-light text-dark'

  const projected = (actualHours + remainingHours) / budgetHours
  if (projected > 1.1) return 'text-bg-danger'
  if (projected > 1.0) return 'text-bg-warning'
  return 'text-bg-secondary'
}

function marginBadgeClass(value: number): string {
  if (value < 0) return 'text-bg-danger'
  if (value < 0.2) return 'text-bg-warning'
  if (value < 0.38) return 'text-bg-secondary'
  return 'text-bg-success'
}

function buildSinceYearOptions(): number[] {
  const shifted = new Date()
  shifted.setMonth(shifted.getMonth() - 3)
  const startYear = shifted.getFullYear()
  return [startYear, startYear - 1, startYear - 2]
}

function formatTypePercent(count: number): string {
  const total = typeCounts.value.reduce((sum, x) => sum + x.Count, 0)
  if (total <= 0) return '0%'
  return formatPercent(count / total)
}

async function loadPage(): Promise<void> {
  try {
    loading.value = true
    error.value = null
    const effectiveSortBy = sortBy.value || (isOpenOnly.value ? 'projectedUtilization' : '')
    const effectiveSortDirection = sortBy.value ? sortDirection.value : (isOpenOnly.value ? 'desc' : sortDirection.value)

    const query = {
      page: page.value,
      pageSize: pageSize.value,
      ownership: ownership.value || undefined,
      eligibilityStatus: eligibilityStatus.value || undefined,
      standingStatus: standingStatus.value || undefined,
      sortBy: effectiveSortBy || undefined,
      sortDirection: effectiveSortDirection || undefined,
      sinceYear: sinceYear.value ? Number(sinceYear.value) : undefined,
      projectType: projectType.value || undefined,
      lifecycle: lifecycle.value,
    }
    const [result, counts] = await Promise.all([
      getProjectStandings(query),
      getProjectStandingTypeCounts(query),
    ])

    items.value = result.Items ?? []
    totalCount.value = result.TotalCount ?? 0
    typeCounts.value = counts ?? []
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Failed to load project standing'
    error.value = message
    items.value = []
    totalCount.value = 0
    typeCounts.value = []
  } finally {
    loading.value = false
  }
}

async function applyFilters(): Promise<void> {
  page.value = 1
  await loadPage()
}

async function resetFilters(): Promise<void> {
  lifecycle.value = 'ClosedOnly'
  ownership.value = ''
  eligibilityStatus.value = ''
  standingStatus.value = ''
  sinceYear.value = ''
  projectType.value = ''
  sortBy.value = ''
  sortDirection.value = 'desc'
  pageSize.value = 100
  page.value = 1
  await loadPage()
}

async function onLifecycleChanged(): Promise<void> {
  if (isOpenOnly.value) {
    sinceYear.value = ''
  }

  if (!isClosedOnly.value) {
    standingStatus.value = ''
  }

  const validSorts = new Set<string>(activeSortByOptions.value.map(x => x.value))
  if (sortBy.value && !validSorts.has(sortBy.value)) {
    sortBy.value = ''
  }

  page.value = 1
  await loadPage()
}

async function goPrev(): Promise<void> {
  if (!canPrev.value) return
  page.value -= 1
  await loadPage()
}

async function goNext(): Promise<void> {
  if (!canNext.value) return
  page.value += 1
  await loadPage()
}

async function goFirst(): Promise<void> {
  if (!canPrev.value) return
  page.value = 1
  await loadPage()
}

async function goLast(): Promise<void> {
  if (!canNext.value) return
  page.value = totalPages.value
  await loadPage()
}

async function goToPage(pageNumber: number): Promise<void> {
  if (pageNumber < 1 || pageNumber > totalPages.value || pageNumber === page.value) return
  page.value = pageNumber
  await loadPage()
}

async function recalculateProject(projectId: number): Promise<void> {
  const next = new Set(recalculatingProjectIds.value)
  next.add(projectId)
  recalculatingProjectIds.value = next

  try {
    await recalculateProjectStanding(projectId)
    await loadPage()
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Failed to recalculate project'
    error.value = message
  } finally {
    const after = new Set(recalculatingProjectIds.value)
    after.delete(projectId)
    recalculatingProjectIds.value = after
  }
}

async function toggleExclusion(row: ProjectStandingRow): Promise<void> {
  const next = new Set(exclusionUpdatingProjectIds.value)
  next.add(row.ProjectId)
  exclusionUpdatingProjectIds.value = next

  try {
    if (row.IsExcluded) {
      await setProjectStandingExclusion(row.ProjectId, false, '')
    } else {
      const input = window.prompt(`Enter ignore reason for project #${row.ProjectId}:`, row.ExcludeReason ?? '')
      if (input === null) return

      const reason = input.trim()
      if (!reason) {
        error.value = 'Ignore reason is required.'
        return
      }

      await setProjectStandingExclusion(row.ProjectId, true, reason)
    }

    await loadPage()
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Failed to update ignore status'
    error.value = message
  } finally {
    const after = new Set(exclusionUpdatingProjectIds.value)
    after.delete(row.ProjectId)
    exclusionUpdatingProjectIds.value = after
  }
}

onMounted(async () => {
  try {
    const me = await getCurrentUserInfo()
    canManageProjectStanding.value = !!me?.CanManageProjectStanding
  } catch {
    canManageProjectStanding.value = false
  }

  try {
    await getProjectStandingGroups()
  } catch {
    // optional for now
  }

  try {
    projectTypeOptions.value = await getProjectStandingProjectTypes()
  } catch {
    projectTypeOptions.value = []
  }

  await loadPage()
})
</script>

<style scoped>
.project-standing-table-wrap {
  overflow-x: auto;
}

.project-standing-table-wrap thead th {
  position: sticky;
  top: 0;
  z-index: 8;
  background-color: var(--bs-light);
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.1);
}

.project-standing-table-wrap table {
  border-collapse: separate;
  border-spacing: 0;
}
</style>
